import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { withAnalytics } from '@ynap/analytics';
import { Dots, Tick } from '@ynap/animations';
import BEMHelper from '@ynap/bem-helper';
import { useMessages, useAppState } from '@ynap/immutables-provider';
import { Notification } from '@ynap/notification';
import Button from '../Button/Button';

const bem = new BEMHelper('QuickAddButton');

const QuickAddButton = (props) => {
    const { addToCart, inProgress, isLastAdded, onMobileQuickAddClick, product } = props;
    const [sizesVisible, setSizesVisible] = useState(false);

    const messages = useMessages();
    const { addToCart: addToCartState } = useAppState();
    const { error, loading } = addToCartState;

    const addToBagError = isLastAdded && error ? messages.notifications?.errors({ error: error?.body?.error?.errorKey }) : '';
    const addToCartLoading = isLastAdded && loading;
    const successfullyAdded = isLastAdded && !addToCartLoading;
    const disabled = addToCartLoading || inProgress;

    const sizes = getSizes(product);
    const oneSizePartNumber = sizes?.length === 1 && sizes[0].isOneSize && sizes[0].partNumber;
    const active = !loading && oneSizePartNumber && !disabled;

    const handleSizeClick = (partNumber) => {
        setSizesVisible(false);
        addToCart(partNumber, product);
    };

    const handleButtonClick = (partNumber) => {
        partNumber ? addToCart(oneSizePartNumber, product) : showSizes();
    };

    const showSizes = () => {
        !loading && !oneSizePartNumber && !successfullyAdded && setSizesVisible(true);
    };

    return (
        <div className={bem()}>
            <div className={bem('quickAddBtn')} onMouseLeave={() => setSizesVisible(false)}>
                {!oneSizePartNumber && (
                    <div className={bem('sizesWrapper', { hidden: !sizesVisible })}>
                        {sizes?.map(
                            ({ available, label, partNumber }) =>
                                available && (
                                    <Button key={partNumber} className={bem('sizeLabel')} onClick={() => handleSizeClick(partNumber)}>
                                        {label}
                                    </Button>
                                ),
                        )}
                    </div>
                )}
                {(() => {
                    if (addToBagError) {
                        return <Notification className={bem('error')} message={addToBagError} modifier="asyncButton" type="error" />;
                    }

                    if (addToCartLoading) {
                        return <Dots className={bem('loadingButton')} type="secondary" />;
                    }

                    if (successfullyAdded) {
                        return <Tick className={bem('tick')} type="quickAdd" />;
                    }

                    return (
                        <Button
                            className={bem('bagPlusButton', { active, disabled, hidden: sizesVisible })}
                            disabled={disabled}
                            onClick={() => handleButtonClick(oneSizePartNumber)}
                            onMouseEnter={showSizes}
                        />
                    );
                })()}
            </div>
            <Button className={bem('bagPlusButton', 'popup')} onClick={onMobileQuickAddClick || addToCart} />
        </div>
    );
};

const getSizes = ({ sKUs }) =>
    sKUs
        ?.filter((sku) => sku.size)
        .map((sku) => {
            const { banned, buyable, displayable, partNumber, size, soldOutOnline } = sku;
            const { centralSizeLabel, labelSize, scaleLabel } = size;

            const available = !banned && buyable && displayable && !soldOutOnline;

            const oneSizeLabels = ['unsized', 'onesize', 'n/a', ''];
            const isOneSize =
                [labelSize, centralSizeLabel, scaleLabel].filter((size) => oneSizeLabels.includes(size?.toLowerCase().replace(/\s/g, ''))).length > 0;

            const label = isOneSize ? undefined : centralSizeLabel || labelSize;

            return {
                available,
                isOneSize,
                label,
                partNumber,
            };
        });

QuickAddButton.propTypes = {
    product: PropTypes.object.isRequired,
    addToCart: PropTypes.func.isRequired,
    onMobileQuickAddClick: PropTypes.func.isRequired,
    isLastAdded: PropTypes.boolean,
    inProgress: PropTypes.boolean,
};

export default withAnalytics(QuickAddButton);
